import React, { useState, useEffect, useMemo } from "react";
import styled from "@emotion/styled";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useRoutingForms } from "./context/RoutingFormsContext";
import RoutingFormsContextWrapper from "./RoutingFormsProvider";
import { useSpace } from "../../../contexts/SpaceContext";
import ReusableTable from "../../../components/Common/ReusableTable";

import {
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Button,
  Checkbox,
  Chip as MuiChip,
  Divider as MuiDivider,
  Grid,
  IconButton,
  Paper as MuiPaper,
  Tooltip,
  Typography,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  TextField,
  DialogActions,
  Alert,
  Card as MuiCard,
  CardContent,
} from "@mui/material";

import {
  Add as AddIcon,
  Launch,
  Link as LinkIcon,
  Edit as EditIcon,
  Code,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { spacing } from "@mui/system";

// Styled components
const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Paper = styled(MuiPaper)(spacing);
const Chip = styled(MuiChip)`
  ${spacing};
`;

// Routing Form Card Component for ReusableTable
const RoutingFormCard = ({
  data: form,
  selected: isSelected,
  onSelect,
  index,
}) => {
  const navigate = useNavigate();

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";

    const options = { year: "numeric", month: "short", day: "numeric" };

    // Handle both timestamp numbers and ISO date strings
    const date = new Date(parseInt(dateString) || dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return "Invalid Date";
    }

    return date.toLocaleDateString(undefined, options);
  };

  const handleEditClick = () => {
    navigate(`/calendar/edit-routing-form/${form._id}`);
  };

  return (
    <MuiCard>
      <CardContent sx={{ position: "relative" }}>
        <Checkbox
          checked={isSelected}
          onChange={(e) => onSelect(e, form._id)}
          sx={{ position: "absolute", top: 8, right: 8 }}
        />

        <Box mb={2}>
          <Typography variant="h6" gutterBottom>
            {form.name}
            {form.status !== "active" && (
              <Chip
                label="Archived"
                size="small"
                variant="outlined"
                sx={{ ml: 1 }}
              />
            )}
          </Typography>

          <Typography variant="body2" color="textSecondary" gutterBottom>
            {form.description || "No description"}
          </Typography>
        </Box>

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mt={2}
        >
          <Typography variant="caption" color="textSecondary">
            Created: {formatDate(form.createdAt)}
          </Typography>

          <Button
            size="small"
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleEditClick}
          >
            Edit
          </Button>
        </Box>
      </CardContent>
    </MuiCard>
  );
};

// Main component wrapped with context provider
function RoutingFormsWithProvider() {
  const location = useLocation();
  const { currentSpace } = useSpace();

  // Get spaceId from query params or use current space
  const queryParams = new URLSearchParams(location.search);
  const currentSpaceId = queryParams.get("spaceId") || currentSpace?.id;

  return (
    <RoutingFormsContextWrapper spaceId={currentSpaceId}>
      <RoutingFormsContent />
    </RoutingFormsContextWrapper>
  );
}

// Content component using routing forms context
function RoutingFormsContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentSpace } = useSpace();
  const {
    forms,
    loading,
    error,
    createForm,
    deleteForm,
    toggleFormStatus,
    refetchForms,
  } = useRoutingForms();

  // Get spaceId from query params or use current space
  const queryParams = new URLSearchParams(location.search);
  const currentSpaceId = queryParams.get("spaceId") || currentSpace?.id;

  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [viewStyle, setViewStyle] = useState("List");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [formInput, setFormInput] = useState({
    title: "",
    description: "",
  });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    severity: "success",
  });
  const [embedDialogOpen, setEmbedDialogOpen] = useState(false);
  const [embedCode, setEmbedCode] = useState("");

  // Format date helper function
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";

    const options = { year: "numeric", month: "short", day: "numeric" };

    // Handle both timestamp numbers and ISO date strings
    const date = new Date(parseInt(dateString) || dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return "Invalid Date";
    }

    return date.toLocaleDateString(undefined, options);
  };

  // Define columns for ReusableTable
  const columns = useMemo(
    () => [
      {
        field: "serialNumber",
        headerName: "S.No",
        width: 70,
        sortable: false,
        filterable: false,
      },
      {
        field: "name",
        headerName: "Title",
        flex: 1.5,
        minWidth: 200,
        renderCell: (params) => (
          <Box>
            <Typography variant="subtitle1" fontWeight="medium">
              {params.row.name}
              {params.row.status !== "active" && (
                <Chip
                  label="Archived"
                  size="small"
                  variant="outlined"
                  sx={{ ml: 1, height: 20 }}
                />
              )}
            </Typography>
          </Box>
        ),
      },
      {
        field: "description",
        headerName: "Description",
        flex: 1,
        minWidth: 150,
        renderCell: (params) => (
          <Typography variant="body2" color="textSecondary">
            {params.row.description || "No description"}
          </Typography>
        ),
      },
      {
        field: "createdAt",
        headerName: "Created On",
        width: 150,
        valueGetter: (params) => formatDate(params.row.createdAt),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 100,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Tooltip title="Edit Form">
            <IconButton
              size="small"
              onClick={() =>
                navigate(`/calendar/edit-routing-form/${params.row._id}`)
              }
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [navigate]
  );

  // Process forms data for ReusableTable
  const processedForms = useMemo(() => {
    return forms.map((form) => ({
      ...form,
      id: form._id, // Add id property for MUI DataGrid
    }));
  }, [forms]);

  // Event handlers
  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = forms.map((n) => n._id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0); // Reset to first page when searching
  };

  const handleCreateFormOpen = () => {
    setCreateDialogOpen(true);
  };

  const handleCreateFormClose = () => {
    setCreateDialogOpen(false);
    setFormInput({ title: "", description: "" });
  };

  const handleCreateForm = async () => {
    try {
      console.log("Creating form with data:", {
        name: formInput.title,
        description: formInput.description,
        spaceId: currentSpaceId,
      });

      const result = await createForm({
        name: formInput.title,
        description: formInput.description,
        // Initialize routing form specific fields
        fields: [], // Form field definitions
        routes: [], // Initialize empty routes
        fallback: {
          type: "CUSTOM_PAGE",
          value: "Thank you for your submission!",
        },
        emailSettings: {
          recipients: [],
          addAllMembers: false,
        },
      });

      console.log("Create form result:", result);

      if (result.success) {
        handleCreateFormClose();

        // Show success notification
        setNotification({
          show: true,
          message: "Form created successfully",
          severity: "success",
        });

        // Redirect to edit page
        navigate(`/calendar/edit-routing-form/${result.form._id}`);
      } else {
        setNotification({
          show: true,
          message: result.error || "Failed to create form",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error creating form:", error);
      setNotification({
        show: true,
        message: error.message || "An error occurred",
        severity: "error",
      });
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      // Delete forms one by one
      const promises = selected.map((id) => deleteForm(id));
      const results = await Promise.all(promises);

      // Check if all deletions were successful
      const allSuccessful = results.every((result) => result.success);

      if (allSuccessful) {
        setNotification({
          show: true,
          message:
            selected.length > 1
              ? `${selected.length} forms deleted successfully`
              : "Form deleted successfully",
          severity: "success",
        });
      } else {
        setNotification({
          show: true,
          message: "Some forms could not be deleted",
          severity: "warning",
        });
      }

      setSelected([]);
      setDeleteConfirmOpen(false);
    } catch (error) {
      console.error("Error deleting forms:", error);
      setNotification({
        show: true,
        message: error.message || "An error occurred while deleting",
        severity: "error",
      });
      setDeleteConfirmOpen(false);
    }
  };

  const handleToggleActive = async () => {
    if (selected.length === 1) {
      const formId = selected[0];
      const form = forms.find((f) => f._id === formId);

      if (form) {
        // Determine the target status - opposite of current
        const isCurrentlyActive = form.status === "active";
        const result = await toggleFormStatus(formId, !isCurrentlyActive);

        if (result.success) {
          setNotification({
            show: true,
            message: `Form ${
              isCurrentlyActive ? "archived" : "activated"
            } successfully`,
            severity: "success",
          });
        } else {
          setNotification({
            show: true,
            message: result.error || "Failed to update form status",
            severity: "error",
          });
        }
      }
    }
  };

  const handleRefresh = async () => {
    try {
      await refetchForms();
      setNotification({
        show: true,
        message: "Forms refreshed successfully",
        severity: "success",
      });
    } catch (err) {
      console.error("Error refreshing forms:", err);
      setNotification({
        show: true,
        message: "Failed to refresh forms",
        severity: "error",
      });
    }
  };

  const handleBulkDelete = () => {
    setDeleteConfirmOpen(true);
  };

  const handleBulkEdit = () => {
    if (selected.length === 1) {
      navigate(`/calendar/edit-routing-form/${selected[0]}`);
    }
  };

  // Handle preview - open preview URL in new tab
  const handlePreview = () => {
    if (selected.length === 1) {
      const formId = selected[0];
      const previewUrl = `/forms/${formId}`;
      window.open(previewUrl, "_blank");
    }
  };

  // Handle copy link - copy preview URL to clipboard
  const handleCopyLink = () => {
    if (selected.length === 1) {
      const formId = selected[0];
      const previewUrl = `/forms/${formId}`;
      const fullUrl = window.location.origin + previewUrl;

      try {
        // Modern clipboard API
        if (navigator.clipboard && window.isSecureContext) {
          navigator.clipboard
            .writeText(fullUrl)
            .then(() => {
              setNotification({
                show: true,
                message: "Link copied to clipboard!",
                severity: "success",
              });
            })
            .catch(() => {
              // Fallback to older method
              copyToClipboardFallback(fullUrl);
            });
        } else {
          // Fallback for older browsers
          copyToClipboardFallback(fullUrl);
        }
      } catch (err) {
        console.error("Copy failed:", err);
        setNotification({
          show: true,
          message: "Failed to copy link",
          severity: "error",
        });
      }
    }
  };

  // Fallback method for copying to clipboard
  const copyToClipboardFallback = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      const successful = document.execCommand("copy");
      document.body.removeChild(textArea);
      if (successful) {
        setNotification({
          show: true,
          message: "Link copied to clipboard!",
          severity: "success",
        });
      } else {
        throw new Error("Copy command was unsuccessful");
      }
    } catch (err) {
      document.body.removeChild(textArea);
      setNotification({
        show: true,
        message: "Failed to copy link. Try selecting and copying manually.",
        severity: "error",
      });
    }
  };

  // Handle embed - show embed dialog
  const handleEmbed = () => {
    if (selected.length === 1) {
      const formId = selected[0];
      const previewUrl = `/forms/${formId}`;
      const fullUrl = window.location.origin + previewUrl;

      // Generate embed code
      const embedHtml = `<iframe src="${fullUrl}" width="100%" height="600" frameborder="0"></iframe>`;
      setEmbedCode(embedHtml);
      setEmbedDialogOpen(true);
    }
  };

  // Handle copy embed code
  const handleCopyEmbedCode = () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(embedCode).then(() => {
          setNotification({
            show: true,
            message: "Embed code copied to clipboard!",
            severity: "success",
          });
        });
      } else {
        copyToClipboardFallback(embedCode);
      }
    } catch (err) {
      console.error("Copy failed:", err);
      setNotification({
        show: true,
        message: "Failed to copy embed code",
        severity: "error",
      });
    }
  };

  // Get custom actions based on selection
  const getCustomActions = () => {
    if (selected.length === 1) {
      const selectedForm = forms.find((f) => f._id === selected[0]);
      return [
        <Tooltip
          key="toggle"
          title={selectedForm?.status === "active" ? "Archive" : "Activate"}
        >
          <Box
            display="flex"
            alignItems="center"
            sx={{
              minWidth: "40px",
              height: "40px",
              justifyContent: "center",
            }}
          >
            <Switch
              checked={selectedForm?.status === "active"}
              onChange={handleToggleActive}
              color="primary"
              size="small"
            />
          </Box>
        </Tooltip>,
        <Tooltip key="preview" title="Preview">
          <IconButton
            aria-label="Preview"
            size="medium"
            onClick={handlePreview}
            sx={{
              minWidth: "40px",
              height: "40px",
            }}
          >
            <Launch />
          </IconButton>
        </Tooltip>,
        <Tooltip key="copy" title="Copy Link">
          <IconButton
            aria-label="Copy Link"
            size="medium"
            onClick={handleCopyLink}
            sx={{
              minWidth: "40px",
              height: "40px",
            }}
          >
            <LinkIcon />
          </IconButton>
        </Tooltip>,
        <Tooltip key="embed" title="Embed">
          <IconButton
            aria-label="Embed"
            size="medium"
            onClick={handleEmbed}
            sx={{
              minWidth: "40px",
              height: "40px",
            }}
          >
            <Code />
          </IconButton>
        </Tooltip>,
        <Tooltip key="refresh" title="Refresh">
          <IconButton
            aria-label="Refresh"
            size="medium"
            onClick={handleRefresh}
            sx={{
              minWidth: "40px",
              height: "40px",
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>,
      ];
    } else if (selected.length === 0) {
      return [
        <Tooltip key="refresh" title="Refresh">
          <IconButton
            aria-label="Refresh"
            size="medium"
            onClick={handleRefresh}
            sx={{
              minWidth: "40px",
              height: "40px",
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>,
      ];
    }
    return [];
  };

  // Clear notification after 5 seconds
  useEffect(() => {
    if (notification.show) {
      const timer = setTimeout(() => {
        setNotification({ ...notification, show: false });
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [notification]);

  return (
    <React.Fragment>
      <Helmet title="Routing Forms" />

      <Grid justifyContent="space-between" container spacing={10}>
        <Grid item>
          <Typography variant="h3" gutterBottom display="inline">
            Routing Forms
          </Typography>

          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <NavLink to={`/spaces/${currentSpaceId}`}>
              {currentSpace?.name || "Spaces"}
            </NavLink>
            <NavLink to={`/calendar/dashboard?spaceId=${currentSpaceId}`}>
              Calendar
            </NavLink>
            <Typography>Routing Forms</Typography>
          </Breadcrumbs>
        </Grid>
      </Grid>

      <Divider my={6} />

      {/* Notification alert */}
      {notification.show && (
        <Alert
          severity={notification.severity}
          sx={{ mb: 3 }}
          onClose={() => setNotification({ ...notification, show: false })}
        >
          {notification.message}
        </Alert>
      )}

      <Grid container spacing={6}>
        <Grid item xs={12}>
          {/* Add New Routing Form Button */}
          <Box display="flex" justifyContent="flex-end" mb={2}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleCreateFormOpen}
            >
              New Routing Form
            </Button>
          </Box>

          {/* Show error if exists */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Error loading forms: {error.message}
            </Alert>
          )}

          {/* ReusableTable */}
          <Paper>
            <ReusableTable
              data={processedForms}
              columns={columns}
              searchQuery={searchQuery}
              onSearchChange={handleSearchChange}
              searchPlaceholder="Search routing forms..."
              searchFields={["name", "description"]}
              selected={selected}
              onSelectAllClick={handleSelectAllClick}
              onSelect={handleClick}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              viewStyle={viewStyle}
              setViewStyle={setViewStyle}
              hasCardView={true}
              CardComponent={RoutingFormCard}
              cardProps={{ xs: 12, sm: 6, md: 4, lg: 3 }}
              onBulkEdit={handleBulkEdit}
              onBulkDelete={handleBulkDelete}
              customActions={getCustomActions()}
              title=""
              showSearch={true}
              loading={loading}
              emptyStateMessage={
                searchQuery
                  ? "No forms match your search criteria"
                  : "No routing forms found"
              }
            />
          </Paper>
        </Grid>
      </Grid>

      {/* Create Form Dialog */}
      <Dialog open={createDialogOpen} onClose={handleCreateFormClose}>
        <DialogTitle>Create New Routing Form</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Create your form to route a booker
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="title"
            label="Title"
            type="text"
            fullWidth
            value={formInput.title}
            onChange={(e) =>
              setFormInput({ ...formInput, title: e.target.value })
            }
          />
          <TextField
            margin="dense"
            id="description"
            label="Description"
            type="text"
            fullWidth
            multiline
            rows={3}
            value={formInput.description}
            onChange={(e) =>
              setFormInput({ ...formInput, description: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCreateFormClose}>Cancel</Button>
          <Button
            onClick={handleCreateForm}
            color="primary"
            disabled={!formInput.title.trim()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete{" "}
            {selected.length > 1
              ? `these ${selected.length} forms`
              : "this form"}
            ? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Embed Code Dialog */}
      <Dialog
        open={embedDialogOpen}
        onClose={() => setEmbedDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Embed Routing Form</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Copy and paste this code into your website to embed the routing
            form:
          </DialogContentText>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={embedCode}
            variant="outlined"
            InputProps={{
              readOnly: true,
            }}
            sx={{ fontFamily: "monospace" }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmbedDialogOpen(false)}>Close</Button>
          <Button
            onClick={handleCopyEmbedCode}
            color="primary"
            variant="contained"
          >
            Copy Code
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}

export default RoutingFormsWithProvider;
